import { useState, useMemo } from 'react';
import {
  Package, Edit, Trash2, Send, Database, ChevronDown, ChevronRight,
  MapPin, Truck as TruckIcon, Calendar, User, Mail, Phone, Weight, DollarSign, Hash,
  CheckSquare, Square, X, Filter, Search,
} from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatDateShort = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

export const LoadsTab = ({ loads, onEdit, onDelete, onBulkDelete, onCreateNew, onNotify, canManageAll }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [selected, setSelected] = useState(new Set());

  // Filter state
  const [sourceFilter, setSourceFilter] = useState('all'); // all | local | tms
  const [statusFilter, setStatusFilter] = useState('all'); // all | available | assigned
  const [dispatcherFilter, setDispatcherFilter] = useState('all'); // all | <email>
  const [searchQuery, setSearchQuery] = useState('');

  // Unique dispatcher list (manager view only — only relevant when seeing multiple agents)
  const dispatcherOptions = useMemo(() => {
    const map = new Map();
    loads.forEach((l) => {
      if (l.agent_email && !map.has(l.agent_email)) {
        map.set(l.agent_email, l.agent_name || l.agent_email);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [loads]);

  // Apply filters
  const filteredLoads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return loads.filter((l) => {
      if (sourceFilter === 'local' && l.source === 'tms') return false;
      if (sourceFilter === 'tms' && l.source !== 'tms') return false;
      if (statusFilter !== 'all' && l.status !== statusFilter) return false;
      if (dispatcherFilter !== 'all' && l.agent_email !== dispatcherFilter) return false;
      if (q) {
        const hay = [
          l.pickup, l.destination, l.commodity, l.truck_type,
          l.reference_no, l.agent_name, l.agent_email,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [loads, sourceFilter, statusFilter, dispatcherFilter, searchQuery]);

  const hasActiveFilters =
    sourceFilter !== 'all' ||
    statusFilter !== 'all' ||
    dispatcherFilter !== 'all' ||
    searchQuery.trim() !== '';

  const resetFilters = () => {
    setSourceFilter('all');
    setStatusFilter('all');
    setDispatcherFilter('all');
    setSearchQuery('');
  };

  const canBulk = !!onBulkDelete;
  // IMPORTANT: bulk operations are scoped to the FILTERED view so managers
  // can isolate a subset (e.g. TMS-only) before "select all".
  const visibleIds = filteredLoads.map((l) => l.id);
  const visibleSelectedCount = visibleIds.filter((id) => selected.has(id)).length;
  const allSelected = canBulk && visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
  const someSelected = canBulk && visibleSelectedCount > 0 && !allSelected;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        // Deselect only the currently visible (filtered) loads
        visibleIds.forEach((id) => next.delete(id));
      } else {
        // Select all currently visible (filtered) loads
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    const ok = window.confirm(`Delete ${selected.size} load${selected.size === 1 ? '' : 's'}? This cannot be undone.`);
    if (!ok) return;
    await onBulkDelete(Array.from(selected));
    setSelected(new Set());
  };

  if (loads.length === 0) {
    return (
      <div className="text-center py-20 bg-white border border-zinc-200">
        <Package className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-zinc-700 mb-2">No loads posted yet</h3>
        <p className="text-zinc-500 mb-6">Start by posting your first load to the board.</p>
        {onCreateNew && (
          <button
            onClick={onCreateNew}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-bold uppercase tracking-wide"
          >
            Post Your First Load
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {canManageAll && (
        <div className="bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-900 leading-relaxed">
          <strong>Manager view:</strong> you&apos;re viewing ALL loads on the board (yours, other dispatchers&apos;, and TMS-synced). Edits to <em>TMS-synced</em> loads will be overwritten by the next 2-minute TMS sync — delete them instead if you want them gone.
        </div>
      )}

      {/* Sticky filter bar — sits just below the global 125px sticky navbar */}
      <div
        className="bg-white border border-zinc-200 px-3 py-3 sticky top-[125px] z-30 shadow-sm"
        data-testid="loads-filter-bar"
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="hidden md:flex items-center gap-1.5 text-zinc-500 pr-1">
            <Filter className="w-3.5 h-3.5" />
            <span className="text-[11px] font-bold uppercase tracking-wider">Filters</span>
          </div>

          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="bg-white border border-zinc-300 text-zinc-900 text-xs font-medium px-2.5 py-2 focus:outline-none focus:border-red-600"
            data-testid="filter-source"
          >
            <option value="all">All Sources</option>
            <option value="local">Local</option>
            <option value="tms">TMS</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border border-zinc-300 text-zinc-900 text-xs font-medium px-2.5 py-2 focus:outline-none focus:border-red-600"
            data-testid="filter-status"
          >
            <option value="all">All Statuses</option>
            <option value="available">Available</option>
            <option value="assigned">Assigned</option>
            <option value="expired">Expired</option>
          </select>

          {canManageAll && dispatcherOptions.length > 1 && (
            <select
              value={dispatcherFilter}
              onChange={(e) => setDispatcherFilter(e.target.value)}
              className="bg-white border border-zinc-300 text-zinc-900 text-xs font-medium px-2.5 py-2 focus:outline-none focus:border-red-600 max-w-[220px]"
              data-testid="filter-dispatcher"
            >
              <option value="all">All Dispatchers</option>
              {dispatcherOptions.map(([email, name]) => (
                <option key={email} value={email}>{name}</option>
              ))}
            </select>
          )}

          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search origin, destination, commodity, ref#…"
              className="w-full bg-white border border-zinc-300 text-zinc-900 text-xs pl-8 pr-2.5 py-2 focus:outline-none focus:border-red-600 placeholder:text-zinc-400"
              data-testid="filter-search"
            />
          </div>

          <div className="ml-auto flex items-center gap-2 text-[11px] text-zinc-500">
            <span data-testid="filter-result-count">
              <strong className="text-zinc-900">{filteredLoads.length}</strong> of {loads.length}
            </span>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="text-zinc-500 hover:text-red-600 font-bold uppercase tracking-wider text-[10px] underline underline-offset-2"
                data-testid="filter-reset"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk action toolbar */}
      {canBulk && selected.size > 0 && (
        <div
          className="bg-zinc-900 text-white px-4 py-3 flex items-center justify-between gap-3 sticky top-[185px] z-20"
          data-testid="bulk-actions-bar"
        >
          <div className="flex items-center gap-3 text-sm">
            <CheckSquare className="w-4 h-4 text-red-500" />
            <span className="font-bold">{selected.size} selected</span>
            {hasActiveFilters && visibleSelectedCount !== selected.size && (
              <span className="text-zinc-400 hidden sm:inline">
                ({visibleSelectedCount} visible in current filter)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete Selected
            </button>
            <button
              onClick={clearSelection}
              className="text-zinc-400 hover:text-white p-2 transition-colors"
              data-testid="bulk-clear-btn"
              title="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="my-loads-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                {canBulk && (
                  <th className="w-10 px-3 py-3">
                    <button
                      onClick={toggleAll}
                      className="text-zinc-400 hover:text-zinc-900 transition-colors"
                      title={allSelected ? 'Deselect all' : 'Select all'}
                      data-testid="select-all-toggle"
                    >
                      {allSelected ? (
                        <CheckSquare className="w-4 h-4 text-red-600" />
                      ) : someSelected ? (
                        <div className="w-4 h-4 border-2 border-red-600 flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-red-600" />
                        </div>
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                )}
                <th className="w-8 px-2 py-3"></th>
                <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Ref #</th>
                <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Route</th>
                <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Pickup</th>
                <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Equipment</th>
                <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider text-zinc-500">Rate</th>
                {canManageAll && (
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Posted By</th>
                )}
                <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Posted</th>
                {canManageAll && (
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Source</th>
                )}
                <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Status</th>
                <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider text-zinc-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filteredLoads.length === 0 ? (
                <tr data-testid="filter-no-results">
                  <td colSpan={(canManageAll ? 11 : 9) + (canBulk ? 1 : 0)} className="px-6 py-12 text-center">
                    <Filter className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                    <p className="text-sm font-bold text-zinc-700 mb-1">No loads match these filters</p>
                    <p className="text-xs text-zinc-500 mb-4">Try adjusting or clearing your filters.</p>
                    <button
                      onClick={resetFilters}
                      className="text-xs font-bold uppercase tracking-wider text-red-600 hover:text-red-700 underline underline-offset-2"
                    >
                      Clear all filters
                    </button>
                  </td>
                </tr>
              ) : filteredLoads.map((load) => {
                const isTms = load.source === 'tms';
                const isOpen = expandedId === load.id;
                const isSelected = selected.has(load.id);
                return (
                  <FragmentRow
                    key={load.id}
                    load={load}
                    isTms={isTms}
                    isOpen={isOpen}
                    isSelected={isSelected}
                    canBulk={canBulk}
                    canManageAll={canManageAll}
                    onToggleSelect={() => toggleOne(load.id)}
                    onToggle={() => setExpandedId(isOpen ? null : load.id)}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onNotify={onNotify}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const FragmentRow = ({ load, isTms, isOpen, isSelected, canBulk, canManageAll, onToggleSelect, onToggle, onEdit, onDelete, onNotify }) => {
  return (
    <>
      <tr
        className={`hover:bg-zinc-50 cursor-pointer ${isSelected ? 'bg-red-50/50' : ''}`}
        onClick={onToggle}
        data-testid={`load-row-${load.reference_no}`}
      >
        {canBulk && (
          <td className="px-3 py-3" onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}>
            <button
              className="text-zinc-400 hover:text-red-600 transition-colors"
              aria-label={isSelected ? 'Deselect load' : 'Select load'}
              data-testid={`select-load-${load.reference_no}`}
            >
              {isSelected ? <CheckSquare className="w-4 h-4 text-red-600" /> : <Square className="w-4 h-4" />}
            </button>
          </td>
        )}
        <td className="px-2 py-3 text-zinc-400">
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-3 py-3 font-mono font-bold text-sm">#{load.reference_no}</td>
        <td className="px-3 py-3">
          <div className="text-sm">
            <div className="font-medium text-zinc-900 truncate max-w-[200px]">{load.pickup}</div>
            <div className="text-zinc-500 truncate max-w-[200px]">&rarr; {load.destination}</div>
          </div>
        </td>
        <td className="px-3 py-3 text-xs text-zinc-600 whitespace-nowrap">
          {formatDateShort(load.pickup_date)}
        </td>
        <td className="px-3 py-3 text-xs text-zinc-600 whitespace-nowrap">
          <span className="inline-flex items-center gap-1">
            <TruckIcon className="w-3 h-3 text-zinc-400" />
            {load.truck_type || '—'}
          </span>
        </td>
        <td className="px-3 py-3 text-right font-bold text-green-600 text-sm whitespace-nowrap">
          {load.rate ? `$${load.rate.toLocaleString()}` : '—'}
        </td>
        {canManageAll && (
          <td className="px-3 py-3">
            <div className="text-xs">
              <div className="font-medium text-zinc-900 truncate max-w-[140px]">{load.agent_name || '—'}</div>
              <div className="text-zinc-500 truncate max-w-[140px]" title={load.agent_email}>{load.agent_email}</div>
            </div>
          </td>
        )}
        <td className="px-3 py-3 text-xs text-zinc-500 whitespace-nowrap">
          {formatDate(load.created_at)}
        </td>
        {canManageAll && (
          <td className="px-3 py-3">
            {isTms ? (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200"
                title="Synced from TMS — edits get overwritten every 2 minutes"
              >
                <Database className="w-2.5 h-2.5" />
                TMS
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-600">
                Local
              </span>
            )}
          </td>
        )}
        <td className="px-3 py-3">
          <span className={`px-2 py-1 text-xs font-bold uppercase ${
            load.status === 'available' ? 'bg-green-100 text-green-700' :
            load.status === 'assigned' ? 'bg-amber-100 text-amber-700' :
            load.status === 'expired' ? 'bg-zinc-200 text-zinc-500 line-through' :
            'bg-zinc-100 text-zinc-700'
          }`}>
            {load.status}
          </span>
        </td>
        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1 justify-end">
            {onNotify && (
              <button
                onClick={() => onNotify(load)}
                className="p-1.5 text-zinc-400 hover:text-green-600 transition-colors"
                title="Notify Carriers"
                data-testid={`notify-load-${load.reference_no}`}
              >
                <Send className="w-4 h-4" />
              </button>
            )}
            {onEdit && (
              <button
                onClick={() => onEdit(load)}
                className="p-1.5 text-zinc-400 hover:text-zinc-900 transition-colors"
                title={isTms ? 'Edit (overwritten by next TMS sync)' : 'Edit'}
                data-testid={`edit-load-${load.reference_no}`}
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(load.id)}
                className="p-1.5 text-zinc-400 hover:text-red-600 transition-colors"
                title={isTms ? 'Delete (reappears unless removed in TMS)' : 'Delete'}
                data-testid={`delete-load-${load.reference_no}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
      </tr>

      {isOpen && (
        <tr className="bg-zinc-50/70 border-t border-zinc-100" data-testid={`load-detail-${load.reference_no}`}>
          <td colSpan={(canManageAll ? 11 : 9) + (canBulk ? 1 : 0)} className="px-6 py-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4 text-sm">
              <DetailItem icon={Hash} label="Reference" value={`#${load.reference_no}`} />
              <DetailItem icon={Package} label="Load Type" value={load.load_type} />
              <DetailItem icon={TruckIcon} label="Equipment" value={load.truck_type} />
              <DetailItem icon={Weight} label="Weight" value={load.weight ? `${Number(load.weight).toLocaleString()} lbs` : '—'} />

              <DetailItem icon={MapPin} label="Pickup" value={load.pickup} />
              <DetailItem icon={MapPin} label="Destination" value={load.destination} />
              <DetailItem icon={Calendar} label="Pickup Date" value={formatDateShort(load.pickup_date)} />
              <DetailItem icon={Calendar} label="Delivery Date" value={formatDateShort(load.delivery_date)} />

              <DetailItem icon={Package} label="Commodity" value={load.commodity} />
              <DetailItem icon={DollarSign} label="Rate" value={load.rate ? `$${load.rate.toLocaleString()}` : '—'} />
              <DetailItem icon={TruckIcon} label="Miles" value={load.miles ? `${load.miles.toLocaleString()} mi` : '—'} />
              <DetailItem icon={Calendar} label="Posted At" value={formatDate(load.created_at)} />

              <DetailItem icon={User} label="Posted By" value={load.agent_name || '—'} />
              <DetailItem icon={Mail} label="Dispatcher Email" value={load.agent_email} link={`mailto:${load.agent_email}`} />
              <DetailItem icon={Phone} label="Dispatcher Phone" value={load.agent_phone} link={`tel:${load.agent_phone}`} />
              <DetailItem icon={Database} label="Source" value={load.source === 'tms' ? 'TMS Sync' : 'Local'} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const DetailItem = ({ icon: Icon, label, value, link }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-1 flex items-center gap-1.5">
      <Icon className="w-3 h-3" />
      {label}
    </div>
    {link && value && value !== '—' ? (
      <a href={link} className="text-zinc-900 font-medium hover:text-red-600 break-all">{value}</a>
    ) : (
      <div className="text-zinc-900 font-medium break-words">{value || '—'}</div>
    )}
  </div>
);
